import React, { useState, useEffect } from 'react';
import { Printer, X, Image, Loader2, Pencil, Save, XCircle, History, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';
import { getDownloadUrlOrOpen } from '@/services/documentsService';
import { getClientGarments, getClientAssignedGarments } from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';
import { useAuth } from '@/contexts/AuthContext';
import { modificarCantidadesAdmin } from '@/services/entregasClientes.service';

const TicketModal = ({ record: recordProp, onClose }) => {
  const { toast } = useToast();
  const { user, isAdmin } = useAuth();
  const [record, setRecord] = useState(recordProp);
  const [isPrinting, setIsPrinting] = useState(false);
  const [evidenciaUrls, setEvidenciaUrls] = useState([]);
  const [loadingEvidencia, setLoadingEvidencia] = useState(false);

  // Admin edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [motivo, setMotivo] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Agregar prendas
  const [availableGarments, setAvailableGarments] = useState([]);
  const [showAddGarment, setShowAddGarment] = useState(false);
  const [loadingGarments, setLoadingGarments] = useState(false);
  const [selectedGarmentId, setSelectedGarmentId] = useState('');

  // Historial trazabilidad
  const [showHistorial, setShowHistorial] = useState(false);

  const printer = getPrinterInstance();

  useEffect(() => {
    setRecord(recordProp);
  }, [recordProp]);

  useEffect(() => {
    if (record.tipo === 'Recoleccion' && Array.isArray(record.evidencias) && record.evidencias.length > 0) {
      setLoadingEvidencia(true);
      Promise.all(
        record.evidencias.map((id) =>
          getDownloadUrlOrOpen({ id, openInNewTab: false }).catch(() => null)
        )
      )
        .then((urls) => setEvidenciaUrls(urls.filter(Boolean)))
        .catch(() => setEvidenciaUrls([]))
        .finally(() => setLoadingEvidencia(false));
    } else {
      setEvidenciaUrls([]);
    }
  }, [record]);

  const isRecol = record.tipo === 'Recoleccion';
  const accentColor = isRecol ? '#ea580c' : '#16a34a';
  const fecha = isRecol
    ? (record.fecha_recoleccion || record.fecha_creacion)
    : (record.fecha_entrega || record.fecha_creacion);
  const totalAlistados = (record.items || []).reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
  const totalConfirmados = (record.items || []).reduce((s, i) => s + (i.cantidad_confirmada ?? 0), 0);

  const handleStartEdit = () => {
    setEditItems((record.items || []).map((item) => ({ ...item })));
    setMotivo('');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditItems([]);
    setMotivo('');
    setShowAddGarment(false);
    setAvailableGarments([]);
    setSelectedGarmentId('');
  };

  const handleItemChange = (index, field, value) => {
    setEditItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: Math.max(0, Number(value) || 0) };
      return next;
    });
  };

  const loadAvailableGarments = async () => {
    if (!record.cliente?._id) return;
    setLoadingGarments(true);
    try {
      const [assignResponse, garmentResponse] = await Promise.all([
        getClientAssignedGarments(record.cliente._id),
        getClientGarments({ limit: 500 }),
      ]);

      const garments = Array.isArray(assignResponse.data) ? assignResponse.data : [];
      const familiaMap = new Map();
      (garmentResponse.data || []).forEach((g) => {
        familiaMap.set(String(g._id), g.familia || null);
      });

      const existingIds = new Set((editItems || []).map((i) => String(i.prendaId)));

      const mapped = garments
        .map((g) => {
          const pid = g.prendaId || g.tipoPrenda?._id || g.tipoPrenda?.id || g._id;
          return {
            prendaId: pid,
            nombre: g.tipoPrenda?.nombre || g.nombre || 'Prenda',
            peso: g.peso || 0,
            familia: familiaMap.get(String(pid)) || null,
          };
        })
        .filter((g) => g.prendaId && !existingIds.has(String(g.prendaId)));

      setAvailableGarments(mapped);
    } catch {
      setAvailableGarments([]);
    } finally {
      setLoadingGarments(false);
    }
  };

  const handleShowAddGarment = async () => {
    setShowAddGarment(true);
    await loadAvailableGarments();
  };

  const handleAddGarment = () => {
    if (!selectedGarmentId) return;
    const garment = availableGarments.find((g) => String(g.prendaId) === String(selectedGarmentId));
    if (!garment) return;

    setEditItems((prev) => [
      ...prev,
      {
        prendaId: garment.prendaId,
        nombre: garment.nombre,
        peso: garment.peso || 0,
        familia: garment.familia || null,
        cantidad_alistada: 0,
        cantidad_confirmada: 0,
        _esNueva: true,
      },
    ]);

    const nextAvailable = availableGarments.filter((g) => String(g.prendaId) !== String(selectedGarmentId));
    setAvailableGarments(nextAvailable);
    setSelectedGarmentId('');
    if (nextAvailable.length === 0) {
      setShowAddGarment(false);
    }
  };

  const handleRemoveNewItem = (index) => {
    setEditItems((prev) => {
      const item = prev[index];
      const next = prev.filter((_, i) => i !== index);
      if (item?._esNueva) {
        setAvailableGarments((ag) => [
          ...ag,
          { prendaId: item.prendaId, nombre: item.nombre, peso: item.peso || 0, familia: item.familia || null },
        ]);
      }
      return next;
    });
  };

  const handleSaveEdit = async () => {
    if (!motivo.trim()) {
      toast({ title: 'Motivo requerido', description: 'Debes indicar el motivo de la modificación.', variant: 'destructive' });
      return;
    }
    setIsSaving(true);
    try {
      const admin = { id: user.id, nombre: user.name || user.raw?.nombre || 'Admin' };
      const itemsToSave = editItems.map(({ _esNueva, ...rest }) => rest);
      const res = await modificarCantidadesAdmin(record._id, itemsToSave, admin, motivo.trim());
      if (res?.data) {
        setRecord(res.data);
        toast({ title: 'Cantidades actualizadas', description: `Modificación registrada por ${admin.nombre}` });
      } else {
        toast({ title: 'Guardado', description: 'Cantidades actualizadas correctamente' });
      }
      setIsEditing(false);
      setEditItems([]);
      setMotivo('');
      setShowAddGarment(false);
      setAvailableGarments([]);
      setSelectedGarmentId('');
    } catch (error) {
      toast({ title: 'Error al guardar', description: error.message || 'No se pudo guardar la modificación', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrint = async () => {
    if (!printer.isBluetoothAvailable()) {
      toast({ title: 'No disponible', description: 'Tu navegador no soporta Bluetooth', variant: 'destructive' });
      return;
    }
    setIsPrinting(true);
    try {
      if (!printer.isConnected()) {
        toast({ title: 'Conectando...', description: 'Selecciona tu impresora Bluetooth' });
        await printer.connect();
      }
      toast({ title: 'Imprimiendo...', description: 'Generando 2 copias (cliente + lavandería)' });

      let familiaMap = new Map();
      try {
        const res = await getClientGarments({ limit: 500 });
        (res.data || []).forEach((g) => familiaMap.set(String(g._id), g.familia || null));
      } catch { /* best-effort */ }

      const enriched = (record.items || []).map((item) => ({
        ...item,
        familia: familiaMap.get(String(item.prendaId)) || null,
      }));
      const recordEnriched = { ...record, items: enriched };

      if (isRecol) {
        await printer.printRecoleccion(recordEnriched);
      } else {
        await printer.printEntrega(recordEnriched);
      }
      toast({ title: 'Listo', description: `Ticket ${record.consecutivo} impreso (2 copias)` });
    } catch (error) {
      const raw = (error?.message || '').toLowerCase();
      const msg = raw.includes('user cancelled') || raw.includes('user denied')
        ? 'Impresión cancelada.'
        : raw.includes('no se pudo conectar') || raw.includes('no hay conexión')
        ? 'No se pudo conectar. Verifica que la impresora esté encendida y cerca.'
        : raw.includes('bluetooth')
        ? 'Error Bluetooth: ' + error.message
        : error.message || 'No se pudo imprimir. Intenta de nuevo.';
      toast({ title: 'Error de impresión', description: msg, variant: 'destructive' });
    } finally {
      setIsPrinting(false);
    }
  };

  const fmtDate = (d) => d
    ? new Date(d).toLocaleString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

  const modificaciones = record.modificaciones_admin || [];

  return (
    <div className="flex flex-col max-h-[90vh]">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b" style={{ background: accentColor }}>
        <div>
          <p className="font-bold text-white text-base">{record.consecutivo}</p>
          <p className="text-xs text-white/80">{isRecol ? 'Recolección de ropa sucia' : 'Entrega de ropa limpia'}</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin() && !isEditing && (
            <button
              onClick={handleStartEdit}
              title="Editar cantidades (Admin)"
              className="text-white/80 hover:text-white p-1 rounded hover:bg-white/10 transition-colors"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-5 space-y-4 font-mono text-sm">
        <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-xs">
          {[
            ['Cliente', record.cliente?.nombre],
            ['NIT', record.cliente?.nit],
            ['Conductor', record.conductor?.nombre],
            ['Recibe / Entrega', record.recibe?.nombre],
            ['Estado', record.estado],
            ['Lavandería', record.lavanderia?.nombre],
            ['Fecha', fmtDate(fecha)],
          ].map(([label, val]) => (
            <React.Fragment key={label}>
              <span className="text-gray-400">{label}:</span>
              <span className="font-medium text-gray-800 truncate">{val || '-'}</span>
            </React.Fragment>
          ))}
        </div>

        {record.observaciones && (
          <div className="bg-yellow-50 border border-yellow-200 rounded px-3 py-2 text-xs text-yellow-800">
            <span className="font-semibold">Obs.: </span>{record.observaciones}
          </div>
        )}

        {(loadingEvidencia || evidenciaUrls.length > 0) && (
          <div className="border-t border-dashed pt-3">
            <p className="text-xs font-bold uppercase text-gray-500 mb-2 flex items-center gap-1">
              <Image className="h-3.5 w-3.5" />
              Evidencias fotográficas
            </p>
            {loadingEvidencia ? (
              <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando evidencias...
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {evidenciaUrls.map((url, idx) => (
                  <button
                    key={idx}
                    onClick={() => window.open(url, '_blank')}
                    className="relative rounded-lg overflow-hidden border border-gray-200 hover:border-purple-400 hover:shadow-md transition-all"
                  >
                    <img src={url} alt={`Evidencia ${idx + 1}`} className="w-full h-20 object-cover" />
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-all" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tabla de prendas */}
        <div className="border-t border-dashed pt-3">
          <p className="text-xs font-bold uppercase text-gray-500 mb-2">Detalle de prendas</p>

          {/* Modo edición admin */}
          {isEditing ? (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs text-amber-800">
                <span className="font-semibold">Modo edición admin</span> — Los cambios quedan registrados con trazabilidad.
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-400 border-b border-dashed">
                    <th className="text-left pb-1 font-medium">Prenda</th>
                    <th className="text-right pb-1 font-medium">Alistado</th>
                    <th className="text-right pb-1 font-medium">Confirmado</th>
                    <th className="text-right pb-1 font-medium w-6" />
                  </tr>
                </thead>
                <tbody>
                  {editItems.map((item, i) => (
                    <tr key={i} className="border-b border-dotted border-gray-100">
                      <td className="py-1 font-medium text-gray-800 flex items-center gap-1">
                        {item.nombre}
                        {item._esNueva && (
                          <span className="text-[10px] bg-green-100 text-green-700 px-1 rounded">NUEVA</span>
                        )}
                      </td>
                      <td className="py-1 text-right">
                        <input
                          type="number"
                          min="0"
                          value={item.cantidad_alistada ?? 0}
                          onChange={(e) => handleItemChange(i, 'cantidad_alistada', e.target.value)}
                          className="w-16 text-right border border-gray-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:border-amber-400"
                        />
                      </td>
                      <td className="py-1 text-right">
                        <input
                          type="number"
                          min="0"
                          value={item.cantidad_confirmada ?? item.cantidad_alistada ?? 0}
                          onChange={(e) => handleItemChange(i, 'cantidad_confirmada', e.target.value)}
                          className="w-16 text-right border border-gray-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:border-amber-400"
                        />
                      </td>
                      <td className="py-1 text-right">
                        {item._esNueva && (
                          <button
                            onClick={() => handleRemoveNewItem(i)}
                            title="Quitar prenda"
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-dashed">
                    <td className="pt-2 font-bold text-gray-700">TOTAL</td>
                    <td className="pt-2 text-right font-bold text-gray-500 text-base">
                      {editItems.reduce((s, i) => s + (i.cantidad_alistada || 0), 0)}
                    </td>
                    <td className="pt-2 text-right font-bold text-gray-900 text-base">
                      {editItems.reduce((s, i) => s + (i.cantidad_confirmada ?? i.cantidad_alistada ?? 0), 0)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>

              {/* Agregar prendas */}
              <div className="space-y-2">
                {!showAddGarment ? (
                  <button
                    onClick={handleShowAddGarment}
                    className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" /> Agregar prenda
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedGarmentId}
                      onChange={(e) => setSelectedGarmentId(e.target.value)}
                      disabled={loadingGarments}
                      className="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:border-amber-400 bg-white"
                    >
                      <option value="">
                        {loadingGarments ? 'Cargando prendas...' : 'Selecciona una prenda'}
                      </option>
                      {availableGarments.map((g) => (
                        <option key={g.prendaId} value={g.prendaId}>
                          {g.nombre}
                        </option>
                      ))}
                    </select>
                    <Button
                      onClick={handleAddGarment}
                      disabled={!selectedGarmentId || loadingGarments}
                      size="sm"
                      className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white px-2"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      onClick={() => { setShowAddGarment(false); setSelectedGarmentId(''); }}
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs px-2"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Campo motivo */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600 uppercase">Motivo de la modificación *</label>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Describe el motivo del ajuste de cantidades..."
                  rows={2}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:border-amber-400 resize-none"
                />
              </div>

              {/* Botones guardar / cancelar */}
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className="flex-1 gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
                >
                  {isSaving
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Guardando...</>
                    : <><Save className="h-3.5 w-3.5" /> Guardar cambios</>}
                </Button>
                <Button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  variant="outline"
                  className="flex-1 gap-1.5 text-xs h-8"
                >
                  <XCircle className="h-3.5 w-3.5" /> Cancelar
                </Button>
              </div>
            </div>
          ) : (
            /* Vista normal */
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 border-b border-dashed">
                  <th className="text-left pb-1 font-medium">Prenda</th>
                  <th className="text-right pb-1 font-medium">Alistado</th>
                  <th className="text-right pb-1 font-medium">Confirmado</th>
                </tr>
              </thead>
              <tbody>
                {(record.items || []).map((item, i) => (
                  <tr key={i} className="border-b border-dotted border-gray-100">
                    <td className="py-1 font-medium text-gray-800">{item.nombre}</td>
                    <td className="py-1 text-right text-gray-400">{item.cantidad_alistada || 0}</td>
                    <td className="py-1 text-right font-bold text-gray-900">{item.cantidad_confirmada ?? item.cantidad_alistada ?? 0}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-dashed">
                  <td className="pt-2 font-bold text-gray-700">TOTAL</td>
                  <td className="pt-2 text-right font-bold text-gray-500 text-base">{totalAlistados}</td>
                  <td className="pt-2 text-right font-bold text-gray-900 text-base">{totalConfirmados}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Historial de modificaciones admin */}
        {modificaciones.length > 0 && (
          <div className="border-t border-dashed pt-3">
            <button
              onClick={() => setShowHistorial((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-bold uppercase text-gray-500 hover:text-gray-700 w-full"
            >
              <History className="h-3.5 w-3.5" />
              Historial de modificaciones admin ({modificaciones.length})
              {showHistorial ? <ChevronUp className="h-3.5 w-3.5 ml-auto" /> : <ChevronDown className="h-3.5 w-3.5 ml-auto" />}
            </button>

            {showHistorial && (
              <div className="mt-2 space-y-2">
                {[...modificaciones].reverse().map((mod, idx) => (
                  <div key={idx} className="bg-gray-50 border border-gray-200 rounded px-3 py-2 text-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-700">{mod.adminNombre}</span>
                      <span className="text-gray-400">{fmtDate(mod.fecha)}</span>
                    </div>
                    <p className="text-gray-600 italic">"{mod.motivo}"</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1">
                      <div>
                        <p className="text-gray-400 uppercase font-medium mb-0.5">Antes</p>
                        {(mod.itemsAnteriores || []).map((it, j) => (
                          <p key={j} className="text-gray-500">
                            {it.nombre}: <span className="font-mono">{it.cantidad_alistada}</span>
                            {it.cantidad_confirmada != null && it.cantidad_confirmada !== it.cantidad_alistada
                              ? <span className="text-gray-400"> / {it.cantidad_confirmada}</span>
                              : null}
                          </p>
                        ))}
                      </div>
                      <div>
                        <p className="text-gray-400 uppercase font-medium mb-0.5">Después</p>
                        {(mod.itemsNuevos || []).map((it, j) => (
                          <p key={j} className="text-gray-700 font-medium">
                            {it.nombre}: <span className="font-mono">{it.cantidad_alistada}</span>
                            {it.cantidad_confirmada != null && it.cantidad_confirmada !== it.cantidad_alistada
                              ? <span className="text-gray-500"> / {it.cantidad_confirmada}</span>
                              : null}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-500 text-center">
          Se imprimirán <strong>2 copias</strong>: una para el cliente y otra para la lavandería
        </div>
      </div>

      {/* Footer */}
      {!isEditing && (
        <div className="border-t px-5 py-3">
          <Button onClick={handlePrint} disabled={isPrinting} className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white">
            {isPrinting
              ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Imprimiendo...</>
              : <><Printer className="h-4 w-4" /> Imprimir (2 copias — Bluetooth)</>}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TicketModal;
